import { makeAutoObservable, observe, flow } from "mobx";
import { RootStore } from "../../store";
import {
  getApi,
  getPublicApi,
  login,
  apiUploadFile,
} from "../../api/apiClient";
import { tokenManager } from "../../api/tokenManager";
import { User } from "../../api/graphql";
import * as _ from "lodash";
import { AuthStoreInterface } from "./AuthStoreInterface";
import { imageCache } from "../../common/services/imageCache";
import { Storage } from "@react-force/web-utils";
import { LsKeys } from "../../store/LsKeys";

const authErrors = [
  "emailAndPasswordRequiredError",
  "badEmailOrPasswordError",
] as const;
type errorKeys = typeof authErrors[number];

// Legacy key for migration purposes
const loggedKey = "172349817234908172349087";

// Key for persisting redirect URL across page reloads
const REDIRECT_URL_KEY = "auth_redirect_url";

// User settings types
export type UserSettings = {
  language: "es" | "en" | "pt";
  theme: "light" | "dark";
};

export const DEFAULT_USER_SETTINGS: UserSettings = {
  language: "es",
  // Sale de la configuracion del proyecto, igual que el valor inicial de
  // PrefStore. Los dos tienen que decir lo mismo: cuando vivian separados,
  // cambiar uno y olvidarse del otro no rompia nada visible pero dejaba al
  // usuario sin sesion viendo un modo y al usuario con sesion viendo otro.
  theme: "light",
};

const VALID_LANGUAGES = new Set<string>(["es", "en", "pt"]);
const VALID_THEMES = new Set<string>(["light", "dark"]);

export class AuthStore implements AuthStoreInterface {
  private readonly rootStore: RootStore | null;
  // Dual token fields
  accessToken: string | null = null;
  refreshToken: string | null = null;
  accessExpiresIn: number | null = null;
  refreshExpiresIn: number | null = null;

  status: "anonymous" | "logged" = "anonymous";
  errorKey: errorKeys | null = null;
  email = "";
  password = "";
  newPassword = "";
  secondPassword = "";
  showPassword = false;
  errorMessage = "";
  me: User | null = null;
  logoUrl: string | null = null;
  avatarCacheVersion = 0;
  logInRedirectURL: string | null = null;
  permissions: Set<string> = new Set();
  sessionExpiredDialogOpen = false;
  onLoginSuccessCallback?: () => void;
  // El origen de la audiencia del usuario cuando el login volvió con
  // WRONG_AUDIENCE (null si el server no sugirió ninguno).
  wrongAudienceOrigin: string | null = null;

  // 2FA fields
  twoFaRequired = false;
  twoFaAction: "setup" | "verify" | null = null;
  tempToken: string | null = null;
  twoFaCode = "";
  twoFaSetupData: {
    qrCodeDataUrl: string;
    secret: string;
    backupCodes: string[];
  } | null = null;
  // Opt-in "recordar este dispositivo 10 días": el server solo emite la cookie
  // de device-trust cuando esta flag viaja true en la mutation de verificación.
  rememberDevice = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const self = this;
    authErrors.forEach((err) => {
      observe(rootStore.langStore, (change: any) => {
        if (self.errorKey !== null && self.errorKey === err) {
          self.setErrorMessage(change.newValue as string);
        }
      });
    });
    // Explicitly annotate generators with flow for production builds
    // (makeAutoObservable auto-detection fails after webpack/babel transpilation)
    makeAutoObservable(this, {
      initialize: flow,
      saveProfile: flow,
      updateAvatar: flow,
      loadUserSettings: flow,
      saveUserSettings: flow,
      saveTheme: flow,
      setMyLanguage: flow,
      getMe: flow,
      loggedIn: flow,
      login: flow,
      loginInline: flow,
      changePassword: flow,
      setupTwoFa: flow,
      confirmTwoFaSetup: flow,
      verifyTwoFa: flow,
      verifyTwoFaBackupCode: flow,
    });
    this.initialize();
  }

  *initialize() {
    if (this.status !== "logged") {
      // Try to load from new token manager first
      const authState = tokenManager.getAuthState();
      if (authState.accessToken && !tokenManager.isRefreshTokenExpired()) {
        this.accessToken = authState.accessToken;
        this.refreshToken = authState.refreshToken;
        yield this.loggedIn();
      } else {
        // Fallback: check for legacy token and clear it
        const legacyToken = localStorage.getItem(loggedKey);
        if (legacyToken) {
          localStorage.removeItem(loggedKey);
          // User will need to login again with new system
        }
      }
    }
    return this.status;
  }

  hasLoggedKey() {
    return tokenManager.hasValidTokens();
  }

  hasPermissions(permission?: string | null, roles?: string[]): boolean {
    // Check permission if specified
    if (permission && !this.permissions.has(permission)) {
      return false;
    }

    // Check roles if specified (OR logic - user needs at least one role)
    if (roles && roles.length > 0) {
      const hasRole = roles.some((role) => this.hasRole(role));
      if (!hasRole) {
        return false;
      }
    }

    return true;
  }

  *saveProfile(key: string[], value: any): any {
    const api = yield* getApi(this, this.rootStore!.activityStore);
    yield api.SaveProfile({ key, value });
    this.updateProfile(key, value);
  }

  // Self-service: persiste el idioma de interfaz en el campo user.language (first
  // class), sin permiso de editar User. Actualiza this.me en memoria.
  *setMyLanguage(language: string): any {
    const api = yield* getApi(this, this.rootStore!.activityStore);
    yield api.SetMyLanguage({ language: language as any });
    if (this.me) (this.me as any).language = language;
  }

  *updateAvatar(blob: Blob, filename: string = "avatar.jpg"): any {
    try {
      const file = new File([blob], filename, {
        type: blob.type || "image/jpeg",
      });

      const uploadResult = yield apiUploadFile(
        true,
        file,
        this.rootStore!.activityStore
      );
      const uploadedFile = {
        ticket: uploadResult.singleUpload.ticket,
        filename: uploadResult.singleUpload.filename,
        mimetype: uploadResult.singleUpload.mimetype,
        encoding: uploadResult.singleUpload.encoding,
        size: file.size,
      };

      const api = yield* getApi(this, this.rootStore!.activityStore);
      const result = yield api.UpdateMyAvatar({ avatar: uploadedFile });

      if (result.updateMyAvatar) {
        const user = result.updateMyAvatar as User;

        // Invalidar cache del avatar viejo ANTES de setMe
        const oldAvatarName = this.me?.avatar_data?.name;
        if (oldAvatarName && this.me?.id) {
          imageCache.removeFromCache(
            "User",
            "avatar",
            this.me.id,
            oldAvatarName
          );
        }

        this.setMe(user);
        this.avatarCacheVersion++;
        yield this.loadCompanyLogo();
      }

      return result.updateMyAvatar;
    } catch (error) {
      console.error("Error updating avatar:", error);
      throw error;
    }
  }

  getProfile(key: string[]) {
    if (this.me && this.me.profile) {
      return _.get(this.me.profile, key.join("."));
    }
    return undefined;
  }

  updateProfile(key: string[], value: any) {
    if (this.me && this.me.profile) {
      this.me.profile = _.set(this.me.profile, key.join("."), value);
    }
  }

  *loadUserSettings() {
    if (!this.rootStore) return;

    try {
      let settings = this.getProfile(["__user_settings"]);

      if (!settings || typeof settings !== "object") {
        const localTheme = Storage.get(LsKeys.paletteType, null);
        const localLanguage = Storage.get(LsKeys.language, null);

        if (localTheme || localLanguage) {
          settings = {
            theme: VALID_THEMES.has(localTheme)
              ? localTheme
              : DEFAULT_USER_SETTINGS.theme,
            language: VALID_LANGUAGES.has(localLanguage)
              ? localLanguage
              : DEFAULT_USER_SETTINGS.language,
          };

          try {
            yield this.saveProfile(["__user_settings"], settings);

            if (localTheme) Storage.remove(LsKeys.paletteType);
            if (localLanguage) Storage.remove(LsKeys.language);
          } catch (error) {
            console.error(
              "Failed to migrate settings to profile, keeping localStorage:",
              error
            );
          }
        } else {
          settings = {};
        }
      }

      // Idioma: la fuente de verdad es el campo user.language (first-class).
      // Migración one-time: si quedó un idioma en el JSON legacy (__user_settings),
      // se migra al campo y se limpia del JSON.
      let language = (this.me as any)?.language;
      const legacyLanguage = (settings as any)?.language;
      if (VALID_LANGUAGES.has(legacyLanguage)) {
        language = legacyLanguage;
        try {
          yield this.setMyLanguage(legacyLanguage);
          const cleaned: any = { ...(settings as any) };
          delete cleaned.language;
          yield this.saveProfile(["__user_settings"], cleaned);
        } catch (error) {
          console.error("Failed to migrate language to user field:", error);
        }
      }
      if (!VALID_LANGUAGES.has(language))
        language = DEFAULT_USER_SETTINGS.language;

      const theme = VALID_THEMES.has((settings as any).theme)
        ? (settings as any).theme
        : DEFAULT_USER_SETTINGS.theme;

      this.rootStore.langStore.setLanguage(language as any);
      // El perfil SIEMBRA, no manda: si este dispositivo ya eligio, su
      // eleccion gana. El tema responde al entorno y no a la persona, asi que
      // la misma cuenta puede querer distinto en el escritorio y en el telefono.
      if (!this.rootStore.prefStore.hasDeviceThemeChoice) {
        this.rootStore.prefStore.setTheme(theme as any);
      }
    } catch (error) {
      console.error("Failed to load user settings:", error);
      this.rootStore.langStore.setLanguage(
        DEFAULT_USER_SETTINGS.language as any
      );
      if (!this.rootStore.prefStore.hasDeviceThemeChoice) {
        this.rootStore.prefStore.setTheme(DEFAULT_USER_SETTINGS.theme as any);
      }
    }
  }

  /**
   * Persiste SOLO el tema. Existe aparte de saveUserSettings porque el boton de
   * la barra no toca el idioma, y reusar aquel obligaria a reenviar el idioma
   * en cada cambio de modo — una mutacion de mas y una condicion de carrera de
   * mas si el idioma se esta editando en otra pestania.
   */
  *saveTheme(theme: string): any {
    if (!VALID_THEMES.has(theme)) {
      throw new Error(
        `Invalid theme: ${theme}. Valid values: ${Array.from(VALID_THEMES).join(
          ", "
        )}`
      );
    }
    // Primero la vista, despues el servidor: el cambio de modo se ve al
    // instante y no espera al viaje de ida y vuelta.
    this.rootStore?.prefStore.chooseTheme(theme as any);
    yield this.saveProfile(["__user_settings"], { theme });
  }

  *saveUserSettings(language: string, theme: string) {
    if (!VALID_LANGUAGES.has(language)) {
      throw new Error(
        `Invalid language: ${language}. Valid values: ${Array.from(
          VALID_LANGUAGES
        ).join(", ")}`
      );
    }
    if (!VALID_THEMES.has(theme)) {
      throw new Error(
        `Invalid theme: ${theme}. Valid values: ${Array.from(VALID_THEMES).join(
          ", "
        )}`
      );
    }

    // Idioma → campo user.language (first-class, self-service). Theme → profile JSON.
    yield this.setMyLanguage(language);
    yield this.saveProfile(["__user_settings"], { theme });

    if (this.rootStore) {
      this.rootStore.langStore.setLanguage(language as any);
      // Guardar desde Preferencias SI es una eleccion de este dispositivo: el
      // usuario la hizo a proposito. Va por chooseTheme y no por setTheme.
      this.rootStore.prefStore.chooseTheme(theme as any);
    }
  }

  setErrorMessage(message: string) {
    this.errorMessage = message;
  }

  setTokens(tokens: {
    accessToken: string;
    refreshToken: string;
    accessExpiresIn: number;
    refreshExpiresIn: number;
  }) {
    this.accessToken = tokens.accessToken;
    this.refreshToken = tokens.refreshToken;
    this.accessExpiresIn = tokens.accessExpiresIn;
    this.refreshExpiresIn = tokens.refreshExpiresIn;

    // Save to token manager
    tokenManager.saveTokens(tokens);
  }

  setEmail(email: string) {
    this.email = email;
  }

  setPassword(password: string) {
    this.password = password;
  }

  setNewPassword(password: string) {
    this.newPassword = password;
  }

  setSecondPassword(password: string) {
    this.secondPassword = password;
  }

  get passwordsAreOk() {
    return (
      this.secondPassword === this.newPassword && this.newPassword.length >= 4
    );
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private setAnonymous() {
    this.status = "anonymous";
    this.errorMessage = "";
    this.errorKey = null;
    this.wrongAudienceOrigin = null;
    this.accessToken = null;
    this.refreshToken = null;
    this.accessExpiresIn = null;
    this.refreshExpiresIn = null;
    this.me = null;
    this.permissions = new Set();

    this.logoUrl = null;

    imageCache.clearCache();

    if (this.rootStore?.referenceCacheStore) {
      this.rootStore.referenceCacheStore.invalidateAll();
    }

    tokenManager.clearTokens();
    localStorage.removeItem(loggedKey);
  }

  private setErrorKey(errorKey: errorKeys) {
    if (this.rootStore !== null) {
      this.errorKey = errorKey;
      this.wrongAudienceOrigin = null;
      const lang = this.rootStore.langStore;
      if (lang !== null && this.errorKey in lang) {
        this.errorMessage = lang[this.errorKey as keyof typeof lang] as string;
      }
    }
  }

  // Un login rechazado por audiencia: el texto lo pone el cliente, el server
  // manda el código y el origen. Solo se acepta un origen http(s) sin ruta: es
  // el destino de un enlace.
  private setWrongAudience(error: unknown): boolean {
    const errors = (error as any)?.response?.errors;
    const wrongAudience = Array.isArray(errors)
      ? errors.find((e: any) => e?.extensions?.code === "WRONG_AUDIENCE")
      : undefined;
    if (!wrongAudience) {
      return false;
    }
    const origin = wrongAudience.extensions.expectedOrigin;
    this.wrongAudienceOrigin =
      typeof origin === "string" && /^https?:\/\/[^\s/?#@]+$/i.test(origin)
        ? origin
        : null;
    this.errorKey = null;
    this.errorMessage =
      this.rootStore?.langStore.wrongAudienceMessage(
        this.wrongAudienceOrigin
      ) ?? "";
    return true;
  }

  hasRole(role: string) {
    const rolDelUsuario = this.me?.role?.id;
    return !!rolDelUsuario && rolDelUsuario === role;
  }

  private setMe(me: User) {
    if (this.me && this.me.id === me.id) {
      const role = me.role === undefined ? this.me.role : me.role;
      this.me = { ...this.me, ...me, role };
    } else {
      this.me = me;
    }
    // Solo si el payload trae `role`: updateMyAvatar devuelve el usuario sin
    // rol y no tiene que borrar los permisos. `role: null` sí los vacía.
    if (me.role !== undefined) {
      this.permissions = new Set((me.role?.permissions ?? []).map((x) => x.id));
    }
  }

  *getMe(): any {
    const api = yield* getApi(this, this.rootStore!.activityStore);
    const data = yield api.Me();
    this.setMe(data.me as User);

    // Load company logo if available
    yield this.loadCompanyLogo();

    return this.me;
  }

  async loadCompanyLogo() {
    if (!this.me?.company?.logo_data?.name) {
      return;
    }

    const entityName = "Tenant";
    const fieldName = "logo";
    const entityId = this.me.company.id;
    const fileName = this.me.company.logo_data.name;

    this.logoUrl = await imageCache.getImage(
      entityName,
      fieldName,
      entityId,
      fileName
    );
  }

  logout() {
    if (!this.rootStore) return;

    const storesWithChanges = this.rootStore.storeRegistry.allStores.filter(
      (store) =>
        "hasPendingChanges" in store && (store as any).hasPendingChanges
    );

    if (storesWithChanges.length > 0) {
      this.rootStore.generalErrorTitle =
        this.rootStore.langStore.unsavedChanges || "Cambios sin guardar";
      this.rootStore.generalErrorDescription =
        this.rootStore.langStore.exitWithoutSaving ||
        "¿Salir sin guardar los cambios?";
      this.rootStore.generalErrorOpen = true;
      return;
    }

    if (this.rootStore.systemStatusStore) {
      this.rootStore.systemStatusStore.stopPolling();
    }

    if (this.rootStore.eventSourceStore) {
      this.rootStore.eventSourceStore.disconnect();
    }

    // Limpiar estado de navegación
    this.rootStore.clearBreadcrumbs();
    this.rootStore.closeDrawerView();

    this.setAnonymous();
  }

  setLogIn = () => {
    if (typeof window !== "undefined") {
      const redirectURL = window.location.pathname + window.location.search;
      this.logInRedirectURL = redirectURL;
      localStorage.setItem(REDIRECT_URL_KEY, redirectURL);
    }
  };

  saveToBrowserState() {
    // Token saving is now handled by tokenManager in setTokens()
    // This method is kept for backward compatibility but does nothing
  }

  *loggedIn() {
    this.email = "";
    this.password = "";

    if (this.rootStore !== null) {
      this.logInRedirectURL = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem(REDIRECT_URL_KEY);
      }

      try {
        yield this.getMe();
        yield this.loadUserSettings();
        this.status = "logged";
        this.rootStore.prefStore.setSideBarOpen(true);

        if (this.rootStore.systemStatusStore) {
          this.rootStore.systemStatusStore.startPolling();
        }

        if (this.rootStore.eventSourceStore) {
          yield this.rootStore.eventSourceStore.connect();
        }
      } catch (error) {
        this.setAnonymous();
        tokenManager.clearTokens();
      }
    }
  }

  *login() {
    this.wrongAudienceOrigin = null;
    if (this.email !== "" && this.password !== "") {
      this.setAnonymous();
      try {
        yield login(this);
        if (this.twoFaRequired) {
          return;
        }
        if (this.accessToken !== null) {
          yield this.loggedIn();
        } else {
          this.setErrorKey("badEmailOrPasswordError");
        }
      } catch (error) {
        if (this.accessToken === null && !this.setWrongAudience(error)) {
          this.setErrorKey("badEmailOrPasswordError");
        }
      }
    } else {
      this.setErrorKey("emailAndPasswordRequiredError");
    }
  }

  openSessionExpiredDialog(callback?: () => void) {
    this.sessionExpiredDialogOpen = true;
    this.onLoginSuccessCallback = callback;
  }

  *loginInline() {
    this.wrongAudienceOrigin = null;
    if (this.email !== "" && this.password !== "") {
      try {
        yield login(this);
        if (this.accessToken !== null) {
          // Invalidate cache on re-login (data may have changed while session was expired)
          if (this.rootStore?.referenceCacheStore) {
            this.rootStore.referenceCacheStore.invalidateAll();
          }
          this.email = "";
          this.password = "";
          yield this.getMe();
          this.status = "logged";

          if (this.onLoginSuccessCallback) {
            this.onLoginSuccessCallback();
            this.onLoginSuccessCallback = undefined;
          }
          this.sessionExpiredDialogOpen = false;
        } else {
          this.setErrorKey("badEmailOrPasswordError");
        }
      } catch (error) {
        if (this.accessToken === null && !this.setWrongAudience(error)) {
          this.setErrorKey("badEmailOrPasswordError");
        }
      }
    } else {
      this.setErrorKey("emailAndPasswordRequiredError");
    }
  }

  *changePassword(): any {
    if (
      this.password !== "" &&
      this.newPassword !== "" &&
      this.secondPassword !== "" &&
      this.passwordsAreOk
    ) {
      try {
        const api = yield* getApi(this, this.rootStore!.activityStore);
        const data = yield api.ChangePassword({
          oldPassword: this.password,
          newPassword: this.newPassword,
        });
        if (data.userChangePassword.id !== this.me?.id) {
          this.errorMessage = "Error en el cambio de contraseña";
          return;
        }
        this.errorMessage = "";
        this.password = "";
        this.newPassword = "";
        this.secondPassword = "";
      } catch (error) {
        this.errorMessage = "Error al cambiar la contraseña";
      }
    }
  }

  setTwoFaState(state: {
    twoFaRequired: boolean;
    twoFaAction: string | null;
    tempToken: string | null;
  }) {
    this.twoFaRequired = state.twoFaRequired;
    this.twoFaAction = state.twoFaAction as "setup" | "verify" | null;
    this.tempToken = state.tempToken;
  }

  setTwoFaCode(code: string) {
    this.twoFaCode = code;
  }

  setRememberDevice(value: boolean) {
    this.rememberDevice = value;
  }

  clearTwoFaState() {
    this.twoFaRequired = false;
    this.twoFaAction = null;
    this.tempToken = null;
    this.twoFaCode = "";
    this.twoFaSetupData = null;
    this.rememberDevice = false;
  }

  *setupTwoFa(): any {
    if (!this.tempToken) return;
    try {
      const api = getPublicApi();
      const data = yield api.SetupTwoFa({ tempToken: this.tempToken });
      this.twoFaSetupData = data.setupTwoFa;
    } catch (error) {
      console.error("2FA setup error:", error);
      throw error;
    }
  }

  *confirmTwoFaSetup(): any {
    if (!this.tempToken || !this.twoFaCode) return;
    try {
      const api = getPublicApi();
      const data = yield api.ConfirmTwoFaSetup({
        tempToken: this.tempToken,
        code: this.twoFaCode,
        rememberDevice: this.rememberDevice,
      });
      if (data.confirmTwoFaSetup) {
        this.setTokens({
          accessToken: data.confirmTwoFaSetup.accessToken,
          refreshToken: data.confirmTwoFaSetup.refreshToken,
          accessExpiresIn: data.confirmTwoFaSetup.accessExpiresIn,
          refreshExpiresIn: data.confirmTwoFaSetup.refreshExpiresIn,
        });
        this.clearTwoFaState();
        yield this.loggedIn();
      }
    } catch (error) {
      console.error("2FA confirm setup error:", error);
      throw error;
    }
  }

  *verifyTwoFa(): any {
    if (!this.tempToken || !this.twoFaCode) return;
    try {
      const api = getPublicApi();
      const data = yield api.VerifyTwoFa({
        tempToken: this.tempToken,
        code: this.twoFaCode,
        rememberDevice: this.rememberDevice,
      });
      if (data.verifyTwoFa) {
        this.setTokens({
          accessToken: data.verifyTwoFa.accessToken,
          refreshToken: data.verifyTwoFa.refreshToken,
          accessExpiresIn: data.verifyTwoFa.accessExpiresIn,
          refreshExpiresIn: data.verifyTwoFa.refreshExpiresIn,
        });
        this.clearTwoFaState();
        yield this.loggedIn();
      }
    } catch (error) {
      console.error("2FA verify error:", error);
      throw error;
    }
  }

  *verifyTwoFaBackupCode(): any {
    if (!this.tempToken || !this.twoFaCode) return;
    try {
      const api = getPublicApi();
      const data = yield api.VerifyTwoFaBackupCode({
        tempToken: this.tempToken,
        code: this.twoFaCode,
        rememberDevice: this.rememberDevice,
      });
      if (data.verifyTwoFaBackupCode) {
        this.setTokens({
          accessToken: data.verifyTwoFaBackupCode.accessToken,
          refreshToken: data.verifyTwoFaBackupCode.refreshToken,
          accessExpiresIn: data.verifyTwoFaBackupCode.accessExpiresIn,
          refreshExpiresIn: data.verifyTwoFaBackupCode.refreshExpiresIn,
        });
        this.clearTwoFaState();
        yield this.loggedIn();
      }
    } catch (error) {
      console.error("2FA backup code verify error:", error);
      throw error;
    }
  }
}
